// The program as text, the way scripts/knit-along/dat.py writes it: knitout, and LOOP's knitcode
// listing. Both give every course a header naming its DAT row, so a row of the DAT finds its lines.

export type ProgramFormat = "knitcode" | "knitout";

export type ProgramText = {
  readonly lines: readonly string[];
  // every course, in order: its DAT row, counted from 0 at the bottom, and its header's line
  readonly courses: readonly { readonly row: number; readonly line: number }[];
};

const HEADERS: Record<ProgramFormat, RegExp> = {
  // "  412  row 3050  → L1 | …"
  knitcode: /^\s*\d+\s+row\s+(\d+)/,
  // ";; course 412 (DAT row 3050)"
  knitout: /^;; course \d+ \(DAT row (\d+)\)/,
};

export const parseProgram = (text: string, format: ProgramFormat): ProgramText => {
  const lines = text.replace(/\n$/, "").split("\n");
  const courses: { row: number; line: number }[] = [];
  lines.forEach((line, index) => {
    const match = HEADERS[format].exec(line);
    if (match) courses.push({ row: Number(match[1]), line: index });
  });
  return { lines, courses };
};

// The course a DAT row belongs to: the last one starting at or below it; the first below them all.
export const courseAtRow = ({ courses }: ProgramText, row: number): number => {
  let low = 0;
  let high = courses.length - 1;
  if (high < 0 || row <= courses[0].row) return 0;
  while (low < high) {
    const middle = (low + high + 1) >> 1;
    if (courses[middle].row <= row) low = middle;
    else high = middle - 1;
  }
  return low;
};

// A gzipped copy of a file, unpacked here unless the server already did.
export const fetchGzipped = async (src: string): Promise<ArrayBuffer> => {
  const response = await fetch(src);
  if (!response.ok) throw new Error(`${src}: ${response.status}`);
  const bytes = await response.arrayBuffer();
  const head = new Uint8Array(bytes, 0, 2);
  if (head[0] !== 0x1f || head[1] !== 0x8b) return bytes;
  const unpacked = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Response(unpacked).arrayBuffer();
};
